/**
 * Keyboard shortcuts.
 *
 * One registry, two consumers: this installs the listener, and the shortcuts
 * sheet renders the same array. That is deliberate — a sheet written by hand
 * drifts from reality within a week, and the failure is silent in the direction
 * that matters, telling people a key exists when it does not.
 *
 * **Scopes resolve in one order: text entry, then panel, then editor.** Getting
 * that wrong is the classic paint-app bug — typing a layer name containing "b",
 * "e" and "z" switches tools three times and reverts a stroke. Text entry
 * therefore wins outright, and the panel's own keys (arrows, F2, Delete) are
 * handled in panel.ts against a focused row, which never reaches here because
 * this only claims keys the panel does not.
 *
 * See docs/decisions/keys-and-controls.md for which keys and why.
 */

/**
 * A key, as declared rather than as typed.
 *
 * `mod` means Command on a Mac and Control everywhere else. Accepting both
 * unconditionally would make ⌃Z fire on a Mac, where it means nothing, and the
 * cost of getting it right is one boolean.
 */
export interface KeySpec {
  key: string;
  mod?: boolean;
  shift?: boolean;
  alt?: boolean;
  /** Overrides how the sheet writes it. See `formatKeys`. */
  display?: string;
}

export interface Binding extends KeySpec {
  /** Shown in the shortcuts sheet. Imperative: "Undo", not "Undoes". */
  label: string;
  /** Sheet grouping. Order of first appearance decides section order. */
  group: string;
  run: () => void;
  /**
   * Keep this out of the sheet.
   *
   * For aliases — `⌘Y` redoes because Windows says so, but listing it beside
   * `⇧⌘Z` implies a difference that does not exist.
   */
  alias?: boolean;
}

/**
 * Does this event mean this binding?
 *
 * Pure and exported because it is the part that fails quietly. Every modifier
 * is checked in **both** directions: without that, `⌘Z` matches `⇧⌘Z` and
 * pressing redo would undo — the binding declared first simply wins, and which
 * one that is depends on array order nobody thinks about.
 */
export function matchesBinding(spec: KeySpec, e: KeyboardEvent, isMac: boolean): boolean {
  if (e.key.toLowerCase() !== spec.key.toLowerCase()) return false;
  const mod = isMac ? e.metaKey : e.ctrlKey;
  // The other modifier key must be *absent*, or ⌃Z on a Mac — a key combination
  // that means nothing there — would trigger a document-level undo.
  const otherMod = isMac ? e.ctrlKey : e.metaKey;
  if (mod !== Boolean(spec.mod)) return false;
  if (otherMod) return false;
  if (e.shiftKey !== Boolean(spec.shift)) return false;
  if (e.altKey !== Boolean(spec.alt)) return false;
  return true;
}

/**
 * How a shortcut is written down.
 *
 * Mac uses the glyphs with no separator, because that is what every Mac menu
 * does; everywhere else spells it out with `+`. Shown in the sheet and nowhere
 * else, so it never has to round-trip back into a KeySpec.
 */
export function formatKeys(spec: KeySpec, isMac: boolean): string {
  // A binding whose key is itself a shifted character says so already: "?" is
  // clearer than "⇧?", which reads as a third key.
  if (spec.display) return spec.display;
  const key = spec.key.length === 1 ? spec.key.toUpperCase() : spec.key;
  if (isMac) {
    // Apple's order is ⌃⌥⇧⌘ before the key, and it is not arbitrary — every Mac
    // menu is written this way, so ⌘⇧Z reads as a typo to anyone who uses one.
    return `${spec.alt ? "⌥" : ""}${spec.shift ? "⇧" : ""}${spec.mod ? "⌘" : ""}${key}`;
  }
  const parts: string[] = [];
  if (spec.mod) parts.push("Ctrl");
  if (spec.shift) parts.push("Shift");
  if (spec.alt) parts.push("Alt");
  parts.push(key);
  return parts.join("+");
}

/**
 * `<input>` types that are controls rather than fields.
 *
 * Closed and short, where the textual list is open-ended — `text`, `search`,
 * `email`, `number`, `date` and whatever HTML adds next — so the safe default
 * for an unrecognised type is "this takes text", which only costs a shortcut.
 */
const CONTROL_TYPES = new Set([
  "button",
  "checkbox",
  "color",
  "file",
  "hidden",
  "image",
  "radio",
  "range",
  "reset",
  "submit",
]);

/**
 * The check, without a DOM, so it can be tested.
 *
 * `isTypingInto` needs `HTMLInputElement` and the suite runs in node, which is
 * why this guard shipped with one test asserting that null is not typing.
 */
export function takesText(tag: string, type?: string | null, contentEditable = false): boolean {
  if (contentEditable) return true;
  const t = tag.toUpperCase();
  if (t === "TEXTAREA") return true;
  if (t !== "INPUT") return false;
  return !CONTROL_TYPES.has((type ?? "text").toLowerCase());
}

/**
 * True when the focus is somewhere a keystroke means a character.
 *
 * **A checkbox or a slider is not one of those places**, and treating every
 * `<input>` as one turned every shortcut off whenever focus sat on a control:
 * the brush size and opacity sliders have always had this, so tabbing to Size
 * and pressing undo did nothing. It became load-bearing with the faces
 * checklist, which is the keyboard path to the picker — a panel of eighteen
 * checkboxes where undo is dead is not an accessible path to anything.
 *
 * The guard's real purpose is narrower than it looked: stop a layer name
 * containing "b" from switching to the brush. That is about text, and a
 * checkbox accepts none.
 */
export function isTypingInto(el: Element | null): boolean {
  if (!el) return false;
  const editable = el instanceof HTMLElement && el.isContentEditable;
  return takesText(el.tagName, (el as HTMLInputElement).type, editable);
}

/**
 * True when an open dialog owns the keyboard.
 *
 * A modal is its own keyboard context, and the shortcut registry is not part of
 * it. Two things go wrong without this, both found in the blur dialog:
 *
 * - Its slider cannot be driven by arrow keys at all. Arrows are bound to nudge
 *   a placement, `takesText` says a range input is not a field — correctly, so
 *   that shortcuts survive a focused slider — and the binding calls
 *   `preventDefault`. So the slider never moves and something nudges behind the
 *   modal instead.
 * - `Cmd+Z` still undoes. The dialog is holding the pixels it opened with, so
 *   undoing beneath it leaves that snapshot describing a picture that is no
 *   longer there, and Cancel puts back pixels the user had just undone.
 *
 * Deliberately not solved by widening `takesText`: a slider is genuinely not a
 * text field, and every shortcut that works over a focused slider today should
 * keep working. What changed is who owns the keys, not what kind of control has
 * focus.
 */
export function isInsideOpenDialog(el: Element | null): boolean {
  return Boolean(el?.closest?.("dialog[open]"));
}

export interface InstallOptions {
  /** Overridable so the behaviour can be exercised on either platform. */
  isMac?: boolean;
  target?: EventTarget;
}

/**
 * Bind the registry to the document.
 *
 * Returns a teardown, because Vite re-executes this module on every hot update
 * and a second listener would fire every shortcut twice — which for undo means
 * silently losing two steps instead of one.
 */
export function installKeys(bindings: readonly Binding[], opts: InstallOptions = {}) {
  const isMac =
    opts.isMac ?? /mac|iphone|ipad/i.test(globalThis.navigator?.platform ?? navigator.userAgent);
  const target = opts.target ?? window;

  const onKeyDown = (event: Event) => {
    const e = event as KeyboardEvent;
    // Rule one. A shortcut must never beat a person typing a name.
    if (isTypingInto(document.activeElement)) return;
    // Nor a dialog, which is its own keyboard context while it is open.
    if (isInsideOpenDialog(document.activeElement)) return;
    // A key held down repeats; a command should not.
    if (e.repeat) return;

    for (const binding of bindings) {
      if (!matchesBinding(binding, e, isMac)) continue;
      e.preventDefault();
      binding.run();
      return;
    }
  };

  target.addEventListener("keydown", onKeyDown);
  return () => target.removeEventListener("keydown", onKeyDown);
}
